import React, { useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Share2, Check, Linkedin, Mail, ArrowRight } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
type Director = {
  id: number;
  name: string;
  role: string;
  company: string;
  image: string;
  bio: string;
  expertise: string[];
};
const directors: Director[] = [{
  id: 1,
  name: 'Dr. Olumide Emmanuel',
  role: 'Chairman, Board of Directors',
  company: 'Zylus Homes and Property Limited',
  image: 'https://cdn.magicpatterns.com/uploads/7SGbnp221hzRsSFdSYARAf/pasted-image.png',
  bio: `Dr. Olumide Emmanuel is a visionary leader, an accomplished entrepreneur, and a renowned motivational speaker. With a passion for transforming lives and empowering individuals, he has become a beacon of inspiration to people around the world and a doyen in the Real Estate Industry.

He is a leading relationship expert, motivational speaker, and wealth creation agent with over three decades of impactful and transformational career journey. He is an influential personality in the spiritual and secular arena and a pioneer in many fields.

As a highly sought-after speaker, an overseer of a network of churches across 4 continents, and founder of a group of businesses with a global footprint, Dr. Emmanuel has captivated audiences with his dynamic and engaging presentations. His ability to connect with people on a personal level, combined with his profound insights and practical wisdom, leaves a lasting impact on everyone who has the privilege of hearing him speak.

Dr. Olumide's journey to success is nothing short of remarkable. Born and raised in Nigeria, he overcame numerous challenges and adversities to achieve remarkable feats. His unwavering determination, coupled with his unyielding belief in the potential of the human spirit, enabled him to rise above his circumstances and create a life of purpose and abundance.

He is an alumnus of the University of Huddersfield (UK) and Harvard Business School (USA), where he studied an Executive Masters Certificate in Real Estate with special focus on Capital Market, Project Finance, and Business Strategy. He is presently running his Doctoral Program at Logos University (USA).

As the founder and CEO of many successful business conglomerates, Dr. Olumide has built an empire that spans various industries — including religion, finance, real estate, and media. His entrepreneurial acumen and strategic thinking have yielded significant financial success while providing countless employment opportunities.

Beyond his business ventures, Dr. Emmanuel is deeply committed to philanthropy through the Olumide Emmanuel Foundation and The Entrepreneurship Academy. He actively supports numerous charitable organizations, focusing on education, healthcare, and poverty alleviation.

He is also an accomplished author with several bestselling books — including School of Money, The Common Sense Rule of Money, Breaking the Stronghold of Delay, and Maximizing Opportunities. He is fondly called "The Common Sense Guru" by many of his protégés.

Recognized for his outstanding contributions, Dr. Emmanuel has received numerous accolades and has been invited to speak at prestigious conferences in over 16 countries across Africa, Asia, Europe, and the Americas. He presently sits on the board of 7 companies across Nigeria, Kenya, Rwanda, the United Arab Emirates, the United States, and Canada.`,
  expertise: ['Real Estate Strategy', 'Wealth Creation', 'Corporate Governance', 'Motivational Speaking', 'Mentorship & Coaching', 'Philanthropy', 'Authorship', 'Global Leadership']
}, {
  id: 2,
  name: 'Dr. Oluwatosin Olatujoye',
  role: 'Group Chief Executive Officer',
  company: 'Zylus Group International',
  image: 'https://images.unsplash.com/photo-1622253692010-333f2da6031d?w=600&q=85',
  bio: 'Founder and Group CEO of Zylus Group International, Dr. Olatujoye is a serial entrepreneur with deep expertise in real estate development, corporate finance, and pan-African investment. Under his leadership, Zylus has grown from a single estate developer into a multi-vertical group spanning property, technology, and lifestyle.',
  expertise: ['Real Estate Development', 'Group Strategy', 'Investment', 'Pan-African Expansion']
}, {
  id: 3,
  name: 'Bukola Olatujoye',
  role: 'Chief Executive Officer',
  company: 'Zylus Homes and Property Limited',
  image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=600&q=85',
  bio: 'As CEO of Zylus Homes and Property, Bukola Olatujoye drives the day-to-day operations and customer experience that have made Zylus one of the most trusted real estate brands in Nigeria. Her sharp eye for product, brand, and client service shapes every estate the company brings to market.',
  expertise: ['Operations', 'Customer Experience', 'Brand Strategy', 'People Leadership']
}, {
  id: 4,
  name: 'Wasiu Sonekan',
  role: 'Executive Director, Finance',
  company: 'Zylus Group',
  image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&q=85',
  bio: 'A chartered finance professional with over fifteen years across banking and real estate, Wasiu Sonekan oversees financial strategy, risk, and capital structure at Zylus Group. He ensures every Zylus development is built on disciplined financial foundations.',
  expertise: ['Corporate Finance', 'Risk Management', 'Capital Markets', 'Audit & Controls']
}, {
  id: 5,
  name: 'Lateef Lawal',
  role: 'Managing Director',
  company: 'Zylus Homes and Property Limited',
  image: 'https://images.unsplash.com/photo-1600486913747-55e5470d6f40?w=600&q=85',
  bio: 'Managing Director of Zylus Homes, Lateef Lawal leads the property and project delivery teams. With a strong background in construction and development management, he is the driving force behind on-time allocations and quality builds across all Zylus estates.',
  expertise: ['Project Delivery', 'Construction Management', 'Estate Development', 'Operations']
}, {
  id: 6,
  name: 'Ijeoma Theo-Nwangwu',
  role: 'Non-Executive Director',
  company: 'Zylus Group',
  image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=600&q=85',
  bio: 'A senior advisor with extensive experience in legal, regulatory, and policy matters, Ijeoma Theo-Nwangwu provides independent oversight on the Zylus board. Her counsel ensures the group remains compliant, ethical, and aligned with international best practices.',
  expertise: ['Legal & Regulatory', 'Governance', 'Policy', 'Independent Oversight']
}];
export function BoardOfDirectors() {
  const [selectedProfile, setSelectedProfile] = useState<Director | null>(null);
  const [copied, setCopied] = useState(false);
  const handleShare = async (d: Director) => {
    const shareData = {
      title: `${d.name} — ${d.role} at Zylus`,
      text: `Meet ${d.name}, ${d.role} at ${d.company}.\n\n${d.bio.slice(0, 140)}…`,
      url: typeof window !== 'undefined' ? window.location.href : ''
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch {
        /* fallthrough */
      }
    }
    try {
      await navigator.clipboard.writeText(`${shareData.title}\n${shareData.url}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* noop */
    }
  };
  return <div className="pt-28 pb-24" data-id="element-958">
      <PageHeader eyebrow="Leadership" title="Meet Our Board of Directors" subtitle="Our Board of Directors is composed of visionary leaders who guide our strategic direction with expertise and integrity. Each member brings a wealth of experience across various industries." breadcrumb={[{
      label: 'Home',
      href: '/'
    }, {
      label: 'About Us'
    }, {
      label: 'Board of Directors'
    }]} data-id="element-959" />

      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 md:px-12 mt-16" data-id="element-960">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-id="element-961">
          {directors.map((director, i) => <motion.div key={director.id} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i % 3 * 0.1,
          duration: 0.6
        }} whileHover={{
          y: -6
        }} className="bg-white rounded-3xl p-6 sm:p-8 text-center border border-ink/5 shadow-sm hover:shadow-2xl hover:border-gold/30 transition-all duration-500 group" data-id="element-962">
              <div className="relative w-32 h-32 sm:w-40 sm:h-40 mx-auto mb-6" data-id="element-963">
                <motion.div whileHover={{
              scale: 1.05,
              rotate: 2
            }} className="absolute inset-0 rounded-full" data-id="element-964">
                  <div className="absolute inset-0 rounded-full bg-gradient-to-br from-gold/40 to-gold-dark/40 group-hover:from-gold group-hover:to-gold-dark transition-colors duration-500" data-id="element-965" />
                  <div className="absolute inset-1 rounded-full bg-white" data-id="element-966" />
                  <img src={director.image} alt={director.name} className="absolute inset-2 w-[calc(100%-1rem)] h-[calc(100%-1rem)] object-cover rounded-full" loading="lazy" data-id="element-967" />
                </motion.div>
              </div>
              <h3 className="text-xl sm:text-2xl font-serif text-ink mb-2 leading-tight" data-id="element-968">
                {director.name}
              </h3>
              <div className="text-[11px] font-bold text-gold-dark uppercase tracking-[0.2em] mb-2" data-id="element-969">
                {director.role}
              </div>
              <div className="text-xs text-ink/60 mb-8 px-2" data-id="element-970">
                {director.company}
              </div>

              <button onClick={() => setSelectedProfile(director)} className="bg-gold hover:bg-gold-dark text-ink font-bold text-[11px] uppercase tracking-widest px-6 py-3 rounded-full transition-colors inline-flex items-center gap-2" data-id="element-971">
                View Profile
                <ArrowRight className="w-3.5 h-3.5" data-id="element-972" />
              </button>
            </motion.div>)}
        </div>
      </div>

      {/* Profile Modal */}
      <AnimatePresence data-id="element-973">
        {selectedProfile && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => setSelectedProfile(null)} className="fixed inset-0 z-[60] bg-ink/85 backdrop-blur-md flex items-center justify-center p-4 sm:p-6 overflow-y-auto" data-id="element-974">
            <motion.div initial={{
          opacity: 0,
          y: 30,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 30,
          scale: 0.95
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 26
        }} onClick={e => e.stopPropagation()} className="relative w-full max-w-2xl my-auto bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col" data-id="element-975">
              {/* Close */}
              <button onClick={() => setSelectedProfile(null)} aria-label="Close profile" className="absolute top-4 right-4 z-10 w-9 h-9 bg-white/90 hover:bg-gold text-ink rounded-full flex items-center justify-center transition-colors shadow-md" data-id="element-976">
                <X className="w-4 h-4" strokeWidth={2.5} data-id="element-977" />
              </button>

              {/* Top band */}
              <div className="relative bg-gradient-to-br from-ink to-ink/85 px-6 sm:px-10 pt-10 pb-8 text-center" data-id="element-978">
                <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-gold/20 blur-3xl pointer-events-none" data-id="element-979" />
                <div className="relative w-28 h-28 sm:w-32 sm:h-32 mx-auto mb-5" data-id="element-980">
                  <div className="absolute inset-0 rounded-full bg-gradient-to-br from-gold to-gold-dark" data-id="element-981" />
                  <img src={selectedProfile.image} alt={selectedProfile.name} className="absolute inset-1.5 w-[calc(100%-0.75rem)] h-[calc(100%-0.75rem)] object-cover rounded-full border-2 border-white/40" data-id="element-982" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-serif text-white mb-1.5 leading-tight" data-id="element-983">
                  {selectedProfile.name}
                </h3>
                <div className="text-[11px] font-bold text-gold uppercase tracking-[0.25em] mb-1" data-id="element-984">
                  {selectedProfile.role}
                </div>
                <div className="text-xs text-white/60" data-id="element-985">
                  {selectedProfile.company}
                </div>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto px-6 sm:px-10 py-6 sm:py-8 space-y-6" data-id="element-986">
                <div data-id="element-987">
                  <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-gold-dark mb-3" data-id="element-988">
                    About
                  </div>
                  <div className="text-ink/75 text-sm sm:text-[15px] leading-relaxed space-y-4" data-id="element-989">
                    {selectedProfile.bio.split('\n').map(p => p.trim()).filter(Boolean).map((para, idx) => <p key={idx} data-id="element-990">{para}</p>)}
                  </div>
                </div>

                <div data-id="element-991">
                  <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-gold-dark mb-3" data-id="element-992">
                    Areas of Expertise
                  </div>
                  <div className="flex flex-wrap gap-2" data-id="element-993">
                    {selectedProfile.expertise.map(tag => <span key={tag} className="text-xs font-medium text-ink bg-bone border border-ink/10 px-3 py-1.5 rounded-full" data-id="element-994">
                        {tag}
                      </span>)}
                  </div>
                </div>
              </div>

              {/* Footer actions */}
              <div className="border-t border-ink/5 bg-bone/50 px-6 sm:px-10 py-4 flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-center sm:justify-between" data-id="element-995">
                <div className="flex items-center gap-2 text-ink/40" data-id="element-996">
                  <button aria-label="LinkedIn (coming soon)" className="w-9 h-9 rounded-full bg-white border border-ink/10 flex items-center justify-center hover:bg-ink hover:text-white hover:border-ink transition-colors" data-id="element-997">
                    <Linkedin className="w-4 h-4" data-id="element-998" />
                  </button>
                  <a href="mailto:info@zylushomes.com" aria-label="Email Zylus" className="w-9 h-9 rounded-full bg-white border border-ink/10 flex items-center justify-center text-ink/60 hover:bg-ink hover:text-white hover:border-ink transition-colors" data-id="element-999">
                    <Mail className="w-4 h-4" data-id="element-1000" />
                  </a>
                </div>

                <motion.button whileHover={{
              y: -2
            }} whileTap={{
              scale: 0.97
            }} onClick={() => handleShare(selectedProfile)} className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-dark text-ink font-bold text-[11px] uppercase tracking-widest px-5 py-3 rounded-xl transition-colors" data-id="element-1001">
                  {copied ? <>
                      <Check className="w-3.5 h-3.5" data-id="element-1002" /> Copied
                    </> : <>
                      <Share2 className="w-3.5 h-3.5" data-id="element-1003" /> Share Profile
                    </>}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}